import React, { useEffect, useState } from 'react';
import {
  Image,
  Platform,
  SectionList,
  SectionListData,
  StyleProp,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';

import { useBillStatementsQuery } from 'apollo/queries/autogenerate/hooks';
import {
  BillStatement,
  BillStatementStatusV2,
} from 'apollo/queries/autogenerate/schemas';
import IconOverdue from 'shared/assets/images/bill/circled-statement-overdue.png';
import IconOverdueWeb from 'shared/assets/images/bill/circled-statement-overdue@3x.png';
import IconStatement from 'shared/assets/images/bill/circled-statement.png';
import IconStatementWeb from 'shared/assets/images/bill/circled-statement@3x.png';
import generateID from 'shared/modules/common/utils/generate-id';
import { formatToCurrency } from 'shared/utils/japanese';

const IS_WEB = process.env.APP_ENV === 'web';

/**
 * Returns the appropriate icon and message for the BillStatement
 */
const showBillStatusVisualCue = (billStatement: BillStatement) => {
  let message = '請求確定';
  let icon = IS_WEB ? IconStatementWeb : IconStatement;

  if (!billStatement.amount) {
    message = '請求なし';
  } else {
    if (billStatement.statusV2 === BillStatementStatusV2.PAID) {
      message = '支払済み';
    }

    if (billStatement.statusV2 === BillStatementStatusV2.OVERDUE) {
      message = 'お支払い待ち';
      icon = IS_WEB ? IconOverdueWeb : IconOverdue;
    }
  }

  return { message, icon };
};

type Props = {
  onItemPress?: (billStatement: BillStatement) => void;
  style?: StyleProp<ViewStyle>;
};

const BillStatements = (props: Props) => {
  const { data, loading } = useBillStatementsQuery({
    fetchPolicy: 'cache-and-network',
  });
  const [formattedData, setFormattedData] = useState<
    SectionListData<BillStatement>[]
  >([]);

  // PAY THE OLDEST UNPAID BILL FIRST
  const formatData = () => {
    const billStatements = [...(data?.billStatements || [])];

    /**
     * WARNING: The backend does not return bills chronologically on some accounts.
     * Until that issue is fixed, this code snippet is a temporary mitigation.
     * Sort the bills in chronological order with the most recent bills on top.
     *
     * This works but this is only a patch on a BE problem and should be fixed on that side ASAP.
     * This has a potentially huge performance impact.
     */
    billStatements.sort((billA, billB) => {
      return new Date(billB.dueAt).getTime() - new Date(billA.dueAt).getTime();
    });

    /**
     * Build something like:
     * {
     *   year2: [month12, month11, ...],
     *   year1: [..., month2, month1],
     *   year0: [..., month2, month1],
     * }
     *
     * The key (year) order is not guaranteed, however.
     */
    type BillStatementsFormatted = {
      [year: string]: BillStatement[];
    };

    const billStatementsFormatted = billStatements.reduce(
      (acc: BillStatementsFormatted, statement) => {
        const year = statement.period.end.substring(0, 4);

        if (!acc[year]) {
          acc[year] = [];
        }

        acc[year].push(statement as BillStatement);

        return acc;
      },
      {}
    );

    // We want to sort the years with the most recent first
    const yearsSorted = Object.keys(billStatementsFormatted).sort(
      (a, b) => parseInt(b, 10) - parseInt(a, 10)
    );

    /**
     * Now that we formatted the billstatements and sorted the years we build it into something like the SectionList can read
     *
     * [
     *   {
     *     title: 'year',
     *     data: [...billStatementsFormatted],
     *     index: <index of section, just for custom styling purposes>
     *   },
     *   ...
     * ]
     */
    const billStatementsGroupedByYear = yearsSorted.map((year, index) => {
      return {
        title: year,
        data: billStatementsFormatted[year],
        index,
      };
    });

    setFormattedData(billStatementsGroupedByYear);
  };

  useEffect(() => {
    if (!loading) {
      formatData();
    }
  }, [loading]);

  if (!loading && !data?.billStatements.length) {
    return (
      <Text>
        現在、発行されているご利用明細はありません。{'\n\n'}
        今月のご利用分についてのご利用明細は、翌月の1日に発行されます。{'\n\n'}
        お支払い方法につきましては、翌月1-3日にメールとSMSにて送信される「お支払いのご案内」に沿って、お済ませください。
      </Text>
    );
  }

  return (
    <SectionList
      sections={formattedData}
      renderItem={({ item, index, section }) => {
        const { message, icon } = showBillStatusVisualCue(item);
        const isPaid = item.statusV2 === BillStatementStatusV2.PAID;
        const isOverDue = item.statusV2 === BillStatementStatusV2.OVERDUE;

        // If the bill is not yet PAID, use this. This amount changes for any user activities
        // e.g partially pay the bill or make refunds.
        // This automatically includes the lateFeeAmount if the bill becomes OVERDUE.
        const outstandingAmount = item.outstandingAmount;

        // Unpaid amount is immutable. This number is created during the bill generation.
        // Do not use this if the bill is still unpaid, because users can choose to partially pay or make refunds.
        // Use this only when bill id PAID.
        const unpaidAmount = item.unpaidAmount;

        // Use only alongside the unpaidAmount when bill is fully paid.
        // So PAID bills should show unpaidAmount + lateFeeAmount
        const lateFeeAmount = item.lateFeeAmount;

        return (
          <TouchableOpacity
            disabled={!item.amount}
            onPress={() => props.onItemPress?.(item)}
            style={[
              style.sectionItem,
              !index && style.sectionItemFirst,
              index === section.data.length - 1 && style.sectionItemLast,
              // Last item of last section will not render a bottom margin
              section.index === formattedData.length - 1 &&
                style.sectionItemLastOfLastSection,
            ]}
            /** Generate HTML and Test IDs  */
            {...generateID(`bill-statements-item-${index}`)}
            /** Platform-specific props */
            {...Platform.select({
              web: {
                accessibilityRole: 'button',
              },
            })}
          >
            <View style={style.sectionItemLeftPart}>
              <Image source={{ uri: icon }} style={style.sectionItemImage} />
              <Text style={style.sectionItemMonthText}>{`${
                new Date(item.period.end).getMonth() + 1
              }月分`}</Text>
            </View>
            <View style={style.sectionItemRightPart}>
              <Text style={style.sectionItemAmountText}>
                {formatToCurrency(
                  isPaid ? unpaidAmount + lateFeeAmount : outstandingAmount
                )}
              </Text>
              <Text
                style={[
                  style.sectionItemStatusText,
                  isOverDue && style.sectionItemStatusTextDanger,
                ]}
              >
                {message}
              </Text>
            </View>
          </TouchableOpacity>
        );
      }}
      renderSectionHeader={(info) => (
        <Text style={style.sectionHeader}>{`${info.section.title}年`}</Text>
      )}
      ItemSeparatorComponent={() => (
        <View style={style.separator}>
          <View style={style.separatorLine} />
        </View>
      )}
      style={props.style}
    />
  );
};

const style = StyleSheet.create({
  sectionHeader: {
    color: '#607D8B',
    fontWeight: 'bold',
    marginBottom: 16,
  },
  sectionItem: {
    backgroundColor: 'white',
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionItemFirst: {
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
  sectionItemLast: {
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    marginBottom: 16,
  },
  sectionItemLastOfLastSection: {
    marginBottom: 0,
  },
  sectionItemLeftPart: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  sectionItemRightPart: {
    alignItems: 'flex-end',
  },
  sectionItemMonthText: {
    color: '#2A2E32',
    fontSize: 12,
    fontWeight: 'bold',
  },
  sectionItemAmountText: {
    color: '#2A2E32',
    fontWeight: 'bold',
    marginBottom: 8,
  },
  sectionItemStatusText: {
    color: '#586065',
    fontSize: 12,
  },
  sectionItemStatusTextDanger: {
    color: '#FF2751',
  },
  sectionItemImage: {
    width: 24,
    height: 24,
    marginRight: 16,
  },
  separator: {
    backgroundColor: 'white',
  },
  separatorLine: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: '#EEF0F2',
    marginHorizontal: 16,
  },
});

export default BillStatements;
